/**
 * SELECTOR INVARIANT for every slice in this directory.
 *
 * A selector must return a VALUE, never a freshly-built function, object or
 * array literal. `useSelector` compares the previous and next result with
 * reference equality, so anything newly allocated per call compares unequal on
 * every store change and force-renders every consumer on every action
 * dispatched anywhere in the app.
 *
 * This is not theoretical: selectors shaped `(state) => (id) => entity` made
 * every row of the (unvirtualized) requests list re-render on every action,
 * which combined with a metadata refetch that grew per page turned "load more"
 * into O(pages^2) work.
 *
 * In practice:
 *  - single entity   -> `xSelectors.selectById(state, id)`
 *  - lookup in a loop -> `xSelectors.selectEntities` (adapter-memoized)
 *  - derived array/object -> `createSelector`, or pass `shallowEqual` as
 *    `useSelector`'s second argument at every call site.
 */
import { combineReducers } from '@reduxjs/toolkit';
import requests from './requests/requestsSlice';
import userSummaries from './userSummaries/userSummariesSlice';
import dashboard from './dashboard/dashboardSlice';
import agentDashboard from './agentDashboard/agentDashboardSlice';
import agentDashboardShares from './agentDashboard/agentDashboardSharesSlice';
import dashboardCard from './dashboard/cardSlice';
import dashboardFilters from './dashboard/dashboardFiltersSlice';
import dashboardViewers from './dashboard/dashboardViewersSlice';
import cardType from './insights/cardTypeSlice';
import insightChartData from './insights/chartDataSlice';
import insightFilterMetadata from './insights/insightFilterMetadataSlice';
import insightDrilldownFilters from './insightDrilldownFilters/insightDrilldownFiltersSlice';
import insightDrilldownMetadata from './insightDrilldownMetadata/insightDrilldownMetadataSlice';
import insightDrilldownProjects from './insightDrilldownProjects/insightDrilldownProjectsSlice';
import insightDrilldownRequests from './insightDrilldownRequests/insightDrilldownRequestsSlice';
import insightDrilldownTasks from './insightDrilldownTasks/insightDrilldownTasksSlice';
import insightDrilldownUsers from './insightDrilldownUsers/insightDrilldownUsersSlice';
import insightDrilldownCapacityManagement from './insightDrilldownCapacityManagement/insightDrilldownCapacityManagmentSlice';
import insightDrilldownEvents from './insightDrilldownEvents/insightDrilldownEventsSlice';
import ldUserSummaries from './userSummaries/LDUserSummariesSlice';
import formSummaries from './formSummaries/formSummariesSlice';
import projectSummaries from './projectSummaries/projectSummariesSlice';
import projectPermissions from './projectPermissions/projectPermissionsSlice';
import businessUnitSummaries from './businessUnitSummaries/businessUnitSummariesSlice';
import requestPermissions from './requestPermissions/requestPermissionsSlice';
import eventPermissions from './eventPermissions/eventPermissionsSlice';
import programPermissions from './programPermissions/programPermissionsSlice';
import tasksList from './tasksList';
import insightFilterOptions from './insightFilterOptions/insightFilterOptionsSlice';

export default combineReducers({
  requests,
  userSummaries,
  dashboard,
  agentDashboard,
  agentDashboardShares,
  dashboardCard,
  dashboardFilters,
  dashboardViewers,
  cardType,
  insightChartData,
  insightFilterMetadata,
  insightFilterOptions,
  insightDrilldownFilters,
  insightDrilldownProjects,
  insightDrilldownRequests,
  insightDrilldownTasks,
  insightDrilldownUsers,
  insightDrilldownCapacityManagement,
  insightDrilldownEvents,
  insightDrilldownMetadata,
  ldUserSummaries,
  formSummaries,
  projectSummaries,
  projectPermissions,
  businessUnitSummaries,
  requestPermissions,
  eventPermissions,
  programPermissions,
  tasksList,
});
